import { useState, useEffect, useRef, useMemo, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Loader2, Send, ChevronLeft, X } from "lucide-react";
import "@/App.css";
import { Toaster, toast } from "sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TrustPillarsSection from "@/components/TrustPillarsSection";
import BreederCTASection from "@/components/BreederCTASection";

import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Search from "@/pages/Search";
import ListingDetail from "@/pages/ListingDetail";
import Inbox from "@/pages/Inbox";
import Thread from "@/pages/Thread";
import Dashboard from "@/pages/Dashboard";
import BreederOnboarding from "@/pages/BreederOnboarding";
import BreederDashboard from "@/pages/BreederDashboard";
import SubscriptionPlans from "@/pages/SubscriptionPlans";
import NewListingForm from "@/pages/NewListingForm";
import BrowseServices from "@/pages/BrowseServices";
import BrowseBreeding from "@/pages/BrowseBreeding";
import BrowseServiceFind from "@/pages/BrowseServiceFind";
import BrowseServiceRegister from "@/pages/BrowseServiceRegister";
import SignIn from "@/pages/SignIn";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import DeveloperPortal from "@/pages/DeveloperPortal";
import BrowseShelters from "@/pages/BrowseShelters";
import BrowseStuds from "@/pages/BrowseStuds";
import BrowseMares from "@/pages/BrowseMares";
import BrowseFarmBreeding from "@/pages/BrowseFarmBreeding";
import Litters from "@/pages/Litters";
import Adoptions from "@/pages/Adoptions";
import ShelterOnboarding from "@/pages/ShelterOnboarding";
import ShelterDashboard from "@/pages/ShelterDashboard";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import TransporterOnboarding from "@/pages/TransporterOnboarding";
import TransporterDashboard from "@/pages/TransporterDashboard";
import BrowseTransporters from "@/pages/BrowseTransporters";
import TransporterDetail from "@/pages/TransporterDetail";
import BrowseAuctions from "@/pages/BrowseAuctions";
import AuctionDetail from "@/pages/AuctionDetail";
import MyAuctions from "@/pages/MyAuctions";
import VendorOnboarding from "@/pages/VendorOnboarding";
import VendorDashboard from "@/pages/VendorDashboard";
import BrowseVendors from "@/pages/BrowseVendors";
import NotFound from "@/pages/NotFound";
import Seo from "@/components/Seo";

// NOTE: Services.js, Breeding.js, ServiceFind.js, ServiceRegister.js are deprecated
// Use BrowseServices, BrowseBreeding, BrowseServiceFind, BrowseServiceRegister instead

import { authApi } from "@/api/auth";
import { logger } from "@/utils/logger";

// ==================== Messaging Safety Helpers ====================
const detectContactInfo = (text) => {
  if (!text) return false;
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const phoneRegex = /[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}/;
  return emailRegex.test(text) || phoneRegex.test(text);
};




// ==================== Auth Context ====================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [breederProfile, setBreederProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const data = await authApi.me();
      setUser(data.user);
      setBreederProfile(data.breeder_profile);
    } catch (error) {
      setUser(null);
      setBreederProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (returnUrl = null) => {
    // Store return URL in sessionStorage if provided
    if (returnUrl) {
      sessionStorage.setItem('authReturnUrl', returnUrl);
    }
    // Native Google OAuth: ask our backend for the consent URL, then redirect.
    const redirectUri = window.location.origin + '/auth/callback';
    try {
      const { auth_url } = await authApi.googleLoginUrl(redirectUri);
      window.location.href = auth_url;
    } catch (error) {
      logger.error("Login error:", error);
      toast.error("Sign-in is temporarily unavailable. Please try again later.");
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      logger.error("Logout error:", error);
    }
    setUser(null);
    setBreederProfile(null);
  };

  const authValue = useMemo(() => ({
    user, breederProfile, loading, login, logout, checkAuth, setUser, setBreederProfile
  }), [user, breederProfile, loading]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== Auth Callback ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setBreederProfile } = useAuth();
  const hasProcessed = useRef(false);

  const processSession = async ({ code, state }) => {
    try {
      const redirectUri = window.location.origin + '/auth/callback';
      const data = await authApi.session({ code, state, redirectUri });
      setUser(data.user);
      setBreederProfile(data.breeder_profile || null);
      toast.success(`Welcome, ${data.user.name}!`);

      // Check for stored return URL
      const returnUrl = sessionStorage.getItem('authReturnUrl');
      sessionStorage.removeItem('authReturnUrl');

      if (returnUrl) {
        navigate(returnUrl, { state: { user: data.user } });
      } else {
        navigate('/dashboard', { state: { user: data.user } });
      }
    } catch (error) {
      logger.error("Auth error:", error);
      toast.error("Authentication failed");
      navigate('/');
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Google redirects back with ?code=...&state=... in the query string
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (code) {
      processSession({ code, state });
    } else {
      navigate('/');
    }
  }, []);


  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-4 animate-spin" />
        <p className="text-[#A1A1AA]">Signing you in...</p>
      </div>
    </div>
  );
};

// ==================== Protected Route ====================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ==================== Navbar (moved to /src/components/Navbar.js) ====================

// ==================== Search Bar (moved to /src/components/SearchBar.js) ====================

// ==================== Category Carousel (moved to /src/components/CategoryCarousel.js) ====================

// ==================== Listings Carousel (moved into /src/pages/Home.js) ====================

// ==================== Listing Card (moved to /src/components/ListingCard.js) ====================

// ==================== Homepage (moved to /src/pages/Home.js) ====================

// ==================== Browse Page (moved to /src/pages/Browse.js) ====================

// ==================== Purchase Section (moved into /src/pages/ListingDetail.js) ====================

// ==================== Listing Detail Page (moved to /src/pages/ListingDetail.js) ====================

// ==================== Search Results Page (moved to /src/pages/Search.js) ====================

// ==================== Dashboard (moved to /src/pages/Dashboard.js) ====================

// ==================== Breeder Onboarding (moved to /src/pages/BreederOnboarding.js) ====================

// ==================== Breeder Dashboard (moved to /src/pages/BreederDashboard.js) ====================

// ==================== New Listing Form (moved to /src/pages/NewListingForm.js) ====================

// ==================== Inbox Page (moved to /src/pages/Inbox.js) ====================

// ==================== Message Thread Page (moved to /src/pages/Thread.js) ====================
// MessageThreadPage component moved to /src/pages/Thread.js

// ==================== Trust Pillars Section ====================
// TrustPillarsSection moved to /src/components/TrustPillarsSection.js

// ==================== Breeder CTA Section ====================
// BreederCTASection moved to /src/components/BreederCTASection.js

// ==================== Footer ====================
// Footer moved to /src/components/Footer.js

// ==================== App Router ====================
const AppRouter = () => {
  const { user, breederProfile, login, logout, checkAuth } = useAuth();
  const [adminToken, setAdminToken] = useState(sessionStorage.getItem("admin_token"));
  const [staffRole, setStaffRole] = useState(sessionStorage.getItem("staff_role") || "admin");
  
  const handleAdminLogin = (token, role = "admin") => {
    setAdminToken(token);
    setStaffRole(role);
  };
  
  const handleAdminLogout = () => {
    setAdminToken(null);
    setStaffRole(null);
    sessionStorage.removeItem("admin_token");
    sessionStorage.removeItem("staff_role");
  };
  
  return (
    <>
      <Seo />
      <Routes>
      {/* Admin Routes - No navbar/footer */}
      <Route path="/admin" element={<AdminLogin onLogin={handleAdminLogin} />} />
      <Route path="/admin/dashboard" element={<AdminDashboard adminToken={adminToken} staffRole={staffRole} onLogout={handleAdminLogout} />} />
      
      <Route path="/" element={<><Navbar user={user} login={login} logout={logout} /><Home TrustPillarsSection={TrustPillarsSection} BreederCTASection={BreederCTASection} Footer={Footer} user={user} login={login} /></>} />
      <Route path="/signin" element={<><Navbar user={user} login={login} logout={logout} /><SignIn login={login} /></>} />

      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/browse" element={<><Navbar user={user} login={login} logout={logout} /><Browse /></>} />
      <Route path="/browse/:species" element={<><Navbar user={user} login={login} logout={logout} /><Browse /></>} />
      <Route path="/listing/:listingId" element={<><Navbar user={user} login={login} logout={logout} /><ListingDetail user={user} login={login} /></>} />
      <Route path="/search" element={<><Navbar user={user} login={login} logout={logout} /><Search /></>} />
      <Route path="/dashboard" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><Dashboard user={user} breederProfile={breederProfile} /></ProtectedRoute>} />
      <Route path="/dashboard/breeder" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><BreederDashboard breederProfile={breederProfile} checkAuth={checkAuth} /></ProtectedRoute>} />
      <Route path="/subscription-plans" element={<><Navbar user={user} login={login} logout={logout} /><SubscriptionPlans /></>} />
      <Route path="/dashboard/breeder/new-listing" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><NewListingForm breederProfile={breederProfile} /></ProtectedRoute>} />
      <Route path="/onboarding/breeder" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><BreederOnboarding user={user} checkAuth={checkAuth} /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><Inbox /></ProtectedRoute>} />
      <Route path="/messages/:threadId" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><Thread user={user} /></ProtectedRoute>} />
      <Route path="/browse/:species/services" element={<><Navbar user={user} login={login} logout={logout} /><BrowseServices /></>} />
      <Route path="/browse/:species/breeding" element={<><Navbar user={user} login={login} logout={logout} /><BrowseBreeding /></>} />
      <Route path="/browse/:species/services/find" element={<><Navbar user={user} login={login} logout={logout} /><BrowseServiceFind kind="service" /></>} />
      <Route path="/browse/:species/services/register" element={<><Navbar user={user} login={login} logout={logout} /><BrowseServiceRegister kind="service" /></>} />
      <Route path="/browse/:species/breeding/find" element={<><Navbar user={user} login={login} logout={logout} /><BrowseServiceFind kind="breeding" /></>} />
      <Route path="/browse/:species/breeding/register" element={<><Navbar user={user} login={login} logout={logout} /><BrowseServiceRegister kind="breeding" /></>} />
      
      {/* Stud & Mare Services */}
      <Route path="/studs" element={<><Navbar user={user} login={login} logout={logout} /><BrowseStuds /><Footer /></>} />
      <Route path="/mares" element={<><Navbar user={user} login={login} logout={logout} /><BrowseMares /><Footer /></>} />
      <Route path="/farm-breeding" element={<><Navbar user={user} login={login} logout={logout} /><BrowseFarmBreeding /><Footer /></>} />
      
      {/* Litters / Offspring */}
      <Route path="/litters" element={<><Navbar user={user} login={login} logout={logout} /><Litters /><Footer /></>} />
      
      {/* Adoptions */}
      <Route path="/adoptions" element={<><Navbar user={user} login={login} logout={logout} /><Adoptions /><Footer /></>} />
      
      {/* Shelter/Rescue */}
      <Route path="/shelter/register" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><ShelterOnboarding user={user} /></ProtectedRoute>} />
      <Route path="/dashboard/shelter" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><ShelterDashboard user={user} /></ProtectedRoute>} />
      
      {/* Transporter Registry */}
      <Route path="/transporters" element={<><Navbar user={user} login={login} logout={logout} /><BrowseTransporters /><Footer /></>} />
      <Route path="/transporters/:transporterId" element={<><Navbar user={user} login={login} logout={logout} /><TransporterDetail user={user} login={login} /><Footer /></>} />
      <Route path="/transporter/register" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><TransporterOnboarding user={user} /></ProtectedRoute>} />
      <Route path="/dashboard/transporter" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><TransporterDashboard user={user} /></ProtectedRoute>} />
      
      {/* Auctions */}
      <Route path="/auctions" element={<><Navbar user={user} login={login} logout={logout} /><BrowseAuctions /><Footer /></>} />
      <Route path="/auctions/:auctionId" element={<><Navbar user={user} login={login} logout={logout} /><AuctionDetail user={user} login={login} /><Footer /></>} />
      <Route path="/my/auctions" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><MyAuctions user={user} /><Footer /></ProtectedRoute>} />
      <Route path="/vendors" element={<><Navbar user={user} login={login} logout={logout} /><BrowseVendors /><Footer /></>} />
      <Route path="/vendor/register" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><VendorOnboarding user={user} /><Footer /></ProtectedRoute>} />
      <Route path="/dashboard/vendor" element={<ProtectedRoute><Navbar user={user} login={login} logout={logout} /><VendorDashboard user={user} /><Footer /></ProtectedRoute>} />
      
      {/* Legal Pages */}
      <Route path="/privacy-policy" element={<><Navbar user={user} login={login} logout={logout} /><PrivacyPolicy /><Footer /></>} />
      <Route path="/terms-of-service" element={<><Navbar user={user} login={login} logout={logout} /><TermsOfService /><Footer /></>} />
      <Route path="/developers" element={<><Navbar user={user} login={login} logout={logout} /><DeveloperPortal user={user} /><Footer /></>} />
      <Route path="/shelters" element={<><Navbar user={user} login={login} logout={logout} /><BrowseShelters user={user} login={login} /><Footer /></>} />
      <Route path="*" element={<NotFound />} />

      </Routes>
    </>
  );
};

// ==================== Main App ====================
function App() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: "#0A0A0A", color: "#EDEDED", border: "1px solid rgba(255,255,255,0.1)" },
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
